import { MessageCircle, Phone, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND, IMAGES, telLink, telLinkPrimary, waLink } from "@/lib/brand";
import { TESTIDS } from "@/constants/testIds";

export default function Hero() {
  return (
    <section
      data-testid={TESTIDS.heroSection}
      className="relative overflow-hidden bg-[#FAF6EC]"
    >
      {/* Soft cream background with subtle jewellery accent on far right */}
      <img
        src={IMAGES.hero}
        alt=""
        aria-hidden="true"
        className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-25 hidden md:block"
        loading="eager"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,253,248,1) 0%, rgba(255,253,248,0.95) 45%, rgba(255,253,248,0.35) 75%, rgba(255,253,248,0) 100%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy column */}
          <div className="lg:col-span-7 tr-fade-up">
            <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
              <ShieldCheck size={13} strokeWidth={2} />
              Govt. Approved Gold Valuers
            </div>

            <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[0.95] mt-6">
              Cash for Gold.
              <br />
              <span className="italic text-[#B8860B]">Sona Do</span>,{" "}
              <span className="italic text-[#B8860B]">Paisa Lo.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
              Best rate for your jewellery, silver and gold at{" "}
              <span className="text-[#641C24] font-semibold">Top Rate</span>{" "}
              Gurgaon.
            </p>

            {/* PRIMARY PHONE — big, visible above the fold, click-to-call */}
            <a
              href={telLinkPrimary}
              data-testid="hero-primary-phone"
              className="mt-6 inline-flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-5 bg-[#641C24] text-[#FFFDF8] pl-5 pr-6 py-4 sm:py-5 shadow-[0_10px_30px_-10px_rgba(100,28,36,0.55)] hover:bg-[#4A151A] transition-colors w-full sm:w-auto max-w-full"
            >
              <span className="inline-flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 bg-[#D4AF37] text-[#2D181A] shrink-0">
                  <Phone size={18} strokeWidth={2.4} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                  Call Now · अभी कॉल करें
                </span>
              </span>
              <span className="font-display font-black tracking-tight text-3xl sm:text-4xl md:text-5xl leading-none text-[#FFFDF8] tabular-nums">
                {BRAND.phonePrimary}
              </span>
            </a>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                data-testid={TESTIDS.heroWhatsappCta}
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-7 py-4 text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                <MessageCircle size={17} /> Get Best Rate on WhatsApp
              </a>
              <a
                data-testid={TESTIDS.heroCallCta}
                href={telLinkPrimary}
                className="inline-flex items-center justify-center gap-2 border border-[#641C24]/60 hover:bg-[#641C24] hover:text-[#FFFDF8] text-[#641C24] px-7 py-4 text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                <Phone size={17} /> Call {BRAND.phonePrimary}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
              {[
                ["30 min", "Cash Payout"],
                ["2", "Gurgaon Branches"],
                ["999", "Purity Testing"],
                ["North India", "Home Visits"],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="border-l border-[#D4AF37] pl-4"
                >
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#641C24]">
                    {k}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A3B3C] mt-1 font-semibold">
                    {v}
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[#4A3B3C] hover:text-[#641C24] mt-10 text-xs uppercase tracking-[0.26em] font-semibold group"
            >
              Explore All Services{" "}
              <ArrowRight
                size={14}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>

          {/* ₹500 notes visual — right column */}
          <div
            data-testid="hero-rupees"
            className="lg:col-span-5 tr-fade-up flex items-center justify-center relative"
            style={{ animationDelay: "0.15s" }}
          >
            <div className="relative w-full max-w-md">
              {/* Layered ₹500 notes composition */}
              <img
                src={IMAGES.rupees500}
                alt="Stack of ₹500 Indian rupee notes — cash payout at Top Rate"
                loading="eager"
                className="w-full h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.35)] rotate-[-4deg]"
              />
              <img
                src={IMAGES.rupees500}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute -bottom-6 -right-4 w-3/4 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.4)] rotate-[6deg] hidden sm:block"
              />
              {/* Floating pill: "Instant Cash" */}
              <div className="absolute -top-5 -left-4 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                Instant Cash ⚡
              </div>
              <div className="absolute -bottom-2 left-6 sm:left-16 bg-[#D4AF37] text-[#2D181A] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                Bank Transfer OK
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
